// C:\xampp\htdocs\my-project\frontend\src\pages\admin\QuestionBank.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Stack,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Divider,
  IconButton,
  Chip,
  Tooltip,
  Avatar,
  LinearProgress,
  Snackbar,
  Alert,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Badge,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import ImageIcon from "@mui/icons-material/Image";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import QuizIcon from "@mui/icons-material/Quiz";
import PreviewIcon from "@mui/icons-material/Preview";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import api from "../../api/api";

/* ───────────────── UI helpers ───────────────── */
const TYPES = [
  { value: "test", label: "Test" },
  { value: "survey", label: "Survey" },
];
const MODULES = [1, 2, 3, 4, 5];

const Card = ({ children, sx }) => (
  <Paper elevation={2} sx={{ p: 2, borderRadius: 3, ...sx }}>
    {children}
  </Paper>
);

/* ───────────────── Confirm hook (no window.confirm) ───────────────── */
function useConfirm() {
  const [state, setState] = useState({
    open: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    resolve: null,
  });

  const ask = ({
    title,
    message,
    confirmText = "Confirm",
    cancelText = "Cancel",
  }) =>
    new Promise((resolve) =>
      setState({ open: true, title, message, confirmText, cancelText, resolve })
    );

  const handleClose = (val) => {
    state.resolve?.(val);
    setState((s) => ({ ...s, open: false, resolve: null }));
  };

  const dialog = (
    <Dialog open={state.open} onClose={() => handleClose(false)}>
      <DialogTitle>{state.title}</DialogTitle>
      {state.message ? (
        <DialogContent>
          <DialogContentText>{state.message}</DialogContentText>
        </DialogContent>
      ) : null}
      <DialogActions>
        <Button onClick={() => handleClose(false)}>{state.cancelText}</Button>
        <Button
          color="error"
          variant="contained"
          onClick={() => handleClose(true)}
        >
          {state.confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return [ask, dialog];
}

/* ───────────────── Small util ───────────────── */
const correctCount = (q) => (q?.answers || []).filter((a) => a.is_correct)
  .length;
const answerCount = (q) => (q?.answers || []).length;
const isMulti = (q) => correctCount(q) > 1;

/**
 * Normalize question image URL so it hits:
 *   https://members.recc.org.uk/backend/public/storage/...
 * instead of:
 *   https://members.recc.org.uk/backend/storage/...
 */
const resolveImageUrl = (url) => {
  if (!url) return null;

  // Absolute URL case
  if (/^https?:\/\//i.test(url)) {
    // Wrong form: /backend/storage/... -> /backend/public/storage/...
    if (
      url.includes("/backend/storage/") &&
      !url.includes("/backend/public/storage/")
    ) {
      return url.replace(
        "/backend/storage/",
        "/backend/public/storage/"
      );
    }

    // Already correct or some other absolute; leave as is
    return url;
  }

  // Relative path case
  let path = url;

  // Already correct: starts with public/storage
  if (
    path.startsWith("public/storage/") ||
    path.startsWith("/public/storage/")
  ) {
    return path;
  }

  // storage/... -> public/storage/...
  if (path.startsWith("storage/")) {
    path = "public/" + path;
  } else if (path.startsWith("/storage/")) {
    path = "/public" + path;
  }

  return path;
};

export default function QuestionBank() {
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");

  // local filter state for type / module
  const [typeFilter, setTypeFilter] = useState(""); // "" | "test" | "survey"
  const [moduleFilter, setModuleFilter] = useState("");

  const [selected, setSelected] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    sev: "success",
  });
  const [validation, setValidation] = useState("");

  const [askConfirm, ConfirmDialog] = useConfirm();
  const dropRef = useRef(null);

  const filtered = useMemo(() => {
    let rows = Array.isArray(list) ? list : [];
    if (q) {
      const needle = q.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.question_text || "").toLowerCase().includes(needle) ||
          String(r.id).includes(needle)
      );
    }
    if (moduleFilter)
      rows = rows.filter((r) => String(r.module) === String(moduleFilter));
    if (typeFilter)
      rows = rows.filter(
        (r) => String(r.type ?? "test") === String(typeFilter)
      );
    return rows;
  }, [list, q, moduleFilter, typeFilter]);

  const totals = useMemo(
    () => ({
      all: list.length,
      filtered: filtered.length,
      byModule: MODULES.reduce((acc, m) => {
        acc[m] = list.filter((r) => Number(r.module) === Number(m)).length;
        return acc;
      }, {}),
      // counts by type
      byType: TYPES.reduce(
        (acc, t) => {
          acc[t.value] = list.filter(
            (r) => String(r.type ?? "test") === t.value
          ).length;
          return acc;
        },
        { test: 0, survey: 0 }
      ),
    }),
    [list, filtered]
  );

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/questions");
      setList(
        Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : []
      );
    } catch (e) {
      console.error(e);
      setToast({
        open: true,
        msg: "Failed to load questions",
        sev: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelect = (item) => setSelected(JSON.parse(JSON.stringify(item)));

  const onCreate = () => {
    setSelected({
      id: null,
      question_text: "",
      type: "test",
      module: 1,
      image_url: null,
      answers: [],
    });
    setValidation("");
  };

  const validateQuestion = (q) => {
    if (!q?.question_text?.trim()) return "Question text is required.";
    if (!q?.module) return "Module is required.";
    if (q.type === "test") {
      const ans = q.answers || [];
      if (ans.length < 2) return "Test questions must have at least 2 answers.";
      const correct = ans.filter((a) => !!a.is_correct).length;
      if (correct < 1) return "Mark at least one answer as correct.";
    }
    return "";
  };

  const onDeleteQuestion = async (id) => {
    const ok = await askConfirm({
      title: "Delete this question?",
      message: "This action cannot be undone.",
      confirmText: "Delete",
    });
    if (!ok) return;

    try {
      await api.delete(`/admin/questions/${id}`);
      setToast({ open: true, msg: "Question deleted", sev: "success" });
      if (selected?.id === id) setSelected(null);
      await load();
    } catch (e) {
      console.error(e);
      setToast({ open: true, msg: "Delete failed", sev: "error" });
    }
  };

  const onSaveQuestion = async () => {
    const v = validateQuestion(selected);
    if (v) {
      setValidation(v);
      setToast({ open: true, msg: v, sev: "warning" });
      return;
    }

    const fd = new FormData();
    fd.append("question_text", (selected.question_text || "").trim());
    fd.append("type", selected.type ?? "test");
    fd.append("module", String(selected.module ?? 1));

    if (selected._imageFile instanceof File) {
      fd.append("image", selected._imageFile);
    }

    if (selected._removeImage) {
      fd.append("remove_image", "1");
    }

    try {
      let resp;
      if (selected.id) {
        resp = await api.post(`/admin/questions/${selected.id}`, fd, {
          headers: { "X-HTTP-Method-Override": "PATCH" },
        });
      } else {
        resp = await api.post("/admin/questions", fd);
      }

      const saved = resp.data;
      setToast({ open: true, msg: "Question saved", sev: "success" });
      setSelected({ ...saved, _imageFile: null, _removeImage: false });
      await load();
    } catch (e) {
      console.error(e);
      setToast({ open: true, msg: "Save failed", sev: "error" });
    }
  };

  const onAddAnswer = () => {
    if (!selected?.id) {
      setToast({
        open: true,
        msg: "Save the question first.",
        sev: "warning",
      });
      return;
    }
    const newAns = {
      question_id: selected.id,
      answer_text: "",
      is_correct: false,
      _new: true,
    };
    setSelected({
      ...selected,
      answers: [...(selected.answers || []), newAns],
    });
  };

  const onSaveAnswer = async (row, idx) => {
    try {
      let saved;
      if (row.id) {
        const { data } = await api.patch(`/admin/answers/${row.id}`, {
          answer_text: row.answer_text ?? "",
          is_correct: !!row.is_correct,
        });
        saved = data;
      } else {
        const { data } = await api.post(`/admin/answers`, {
          question_id: selected.id,
          answer_text: row.answer_text ?? "",
          is_correct: !!row.is_correct,
        });
        saved = data;
      }
      const next = [...(selected.answers || [])];
      next[idx] = saved;
      setSelected({ ...selected, answers: next });
      setToast({ open: true, msg: "Answer saved", sev: "success" });
      await load();
    } catch (e) {
      console.error(e);
      setToast({ open: true, msg: "Answer save failed", sev: "error" });
    }
  };

  const onDeleteAnswer = async (row, idx) => {
    const ok = await askConfirm({
      title: "Delete this answer?",
      message: "This action cannot be undone.",
      confirmText: "Delete",
    });
    if (!ok) return;

    try {
      if (row.id) await api.delete(`/admin/answers/${row.id}`);
      const next = [...(selected.answers || [])];
      next.splice(idx, 1);
      setSelected({ ...selected, answers: next });
      setToast({ open: true, msg: "Answer deleted", sev: "success" });
      await load();
    } catch (e) {
      console.error(e);
      setToast({
        open: true,
        msg: "Answer delete failed",
        sev: "error",
      });
    }
  };

  /* ────────────── drag & drop for image ────────────── */
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const highlight = (v) => {
      el.style.borderColor = v ? "#1976d2" : "#e5e7eb";
      el.style.background = v ? "rgba(25,118,210,0.06)" : "transparent";
    };
    const prevent = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onDragEnter = (e) => {
      prevent(e);
      highlight(true);
    };
    const onDragOver = (e) => {
      prevent(e);
      highlight(true);
    };
    const onDragLeave = (e) => {
      prevent(e);
      highlight(false);
    };
    const onDrop = (e) => {
      prevent(e);
      highlight(false);
      const f = e.dataTransfer?.files?.[0];
      if (f && selected) {
        setSelected({ ...selected, _imageFile: f, _removeImage: false });
      }
    };
    el.addEventListener("dragenter", onDragEnter);
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragenter", onDragEnter);
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [selected]);

  /* ────────────── preview helpers ────────────── */
  useEffect(() => {
    if (!selected) {
      setPreviewSrc(null);
      return;
    }

    // New local file takes priority
    if (selected._imageFile instanceof File) {
      const url = URL.createObjectURL(selected._imageFile);
      setPreviewSrc(url);

      return () => URL.revokeObjectURL(url);
    }

    // Existing backend image URL
    if (selected.image_url) {
      setPreviewSrc(resolveImageUrl(selected.image_url));
    } else {
      setPreviewSrc(null);
    }
  }, [selected]);

  return (
    <Box
      sx={{ p: { xs: 1.5, md: 3 }, bgcolor: "#f6f8fb", minHeight: "100vh" }}
    >
      {/* Header Toolbar */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <QuizIcon color="primary" />
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, letterSpacing: 0.2 }}
        >
          Question Bank
        </Typography>
        <Chip
          label={`${totals.filtered} of ${totals.all}`}
          size="small"
          sx={{ ml: 1, fontWeight: 600 }}
        />
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<AddIcon />} variant="contained" onClick={onCreate}>
          New Question
        </Button>
      </Stack>

      {/* Quick Filters */}
      <Card sx={{ mb: 2, pt: 1.5, pb: 1.5 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems="center"
        >
          <TextField
            size="small"
            placeholder="Search by text or #id"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />

          {/* Module filter chips */}
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterAltIcon fontSize="small" />
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <Chip
                label="All modules"
                onClick={() => setModuleFilter("")}
                color={moduleFilter === "" ? "primary" : "default"}
                variant={moduleFilter === "" ? "filled" : "outlined"}
                size="small"
              />
              {MODULES.map((m) => (
                <Badge key={m} badgeContent={totals.byModule[m]} color="info">
                  <Chip
                    label={`Module ${m}`}
                    onClick={() => setModuleFilter(String(m))}
                    color={
                      String(moduleFilter) === String(m) ? "primary" : "default"
                    }
                    variant={
                      String(moduleFilter) === String(m)
                        ? "filled"
                        : "outlined"
                    }
                    size="small"
                  />
                </Badge>
              ))}
            </Stack>
          </Stack>

          {/* Type filter chips */}
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterAltIcon fontSize="small" />
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <Chip
                label="All types"
                onClick={() => setTypeFilter("")}
                color={typeFilter === "" ? "primary" : "default"}
                variant={typeFilter === "" ? "filled" : "outlined"}
                size="small"
              />
              {TYPES.map((t) => (
                <Badge
                  key={t.value}
                  badgeContent={totals.byType[t.value]}
                  color="info"
                >
                  <Chip
                    label={t.label}
                    onClick={() => setTypeFilter(t.value)}
                    color={typeFilter === t.value ? "primary" : "default"}
                    variant={typeFilter === t.value ? "filled" : "outlined"}
                    size="small"
                  />
                </Badge>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
        {/* Left: list & filters */}
        <Card sx={{ flex: 1 }}>
          {filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
              <Typography variant="body1">No questions found.</Typography>
              <Typography variant="caption">
                Adjust filters or create a new one.
              </Typography>
            </Box>
          ) : (
            <Stack
              spacing={1}
              sx={{ maxHeight: 640, overflow: "auto", pr: 0.5 }}
            >
              {filtered.map((row) => {
                const c = correctCount(row);
                const a = answerCount(row);
                return (
                  <Paper
                    key={row.id}
                    onClick={() => onSelect(row)}
                    sx={{
                      p: 1.2,
                      borderRadius: 2,
                      cursor: "pointer",
                      border:
                        selected?.id === row.id
                          ? "2px solid #1976d2"
                          : "1px solid #e5e7eb",
                      bgcolor:
                        selected?.id === row.id
                          ? "rgba(25,118,210,0.04)"
                          : "white",
                      transition:
                        "border-color .15s ease, background .15s ease",
                      "&:hover": { borderColor: "#cbd5e1" },
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 46,
                          height: 46,
                          bgcolor: "#0ea5e9",
                          fontWeight: 800,
                        }}
                      >
                        {row.module}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={row.question_text}
                          >
                            #{row.id} · {row.type?.toUpperCase() ?? "TEST"}
                          </Typography>
                          {c > 1 ? (
                            <Chip
                              label="Multi-answer"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="Single"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          <Chip
                            label={`${a} ans`}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>

                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            mt: 0.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {row.question_text}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ mt: 0.75, flexWrap: "wrap" }}
                        >
                          {(row.answers || []).map((ans) => (
                            <Chip
                              key={ans.id ?? Math.random()}
                              size="small"
                              label={ans.answer_text}
                              color={ans.is_correct ? "success" : "default"}
                              variant={
                                ans.is_correct ? "filled" : "outlined"
                              }
                            />
                          ))}
                        </Stack>
                      </Box>

                      <Box sx={{ minWidth: 56, textAlign: "right" }}>
                        {row.image_url ? (
                          <img
                            src={resolveImageUrl(row.image_url)}
                            alt="thumb"
                            style={{
                              width: 56,
                              height: 40,
                              objectFit: "cover",
                              borderRadius: 6,
                              border: "1px solid #e5e7eb",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 56,
                              height: 40,
                              borderRadius: 6,
                              border: "1px dashed #e5e7eb",
                            }}
                          />
                        )}
                        <Tooltip title="Delete question">
                          <IconButton
                            size="small"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await onDeleteQuestion(row.id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Card>

        {/* Right: editor & preview */}
        <Stack sx={{ flex: 1.25 }} spacing={2}>
          <Card>
            {selected ? (
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {selected.id
                      ? `Edit Question #${selected.id}`
                      : "Create Question"}
                  </Typography>
                  <Chip
                    size="small"
                    color={isMulti(selected) ? "warning" : "default"}
                    label={isMulti(selected) ? "Multi-answer" : "Single-answer"}
                    variant="outlined"
                  />
                </Stack>

                <TextField
                  label="Question text"
                  multiline
                  minRows={3}
                  value={selected.question_text ?? ""}
                  onChange={(e) =>
                    setSelected({
                      ...selected,
                      question_text: e.target.value,
                    })
                  }
                />
                {validation && <FormHelperText error>{validation}</FormHelperText>}

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                >
                  <TextField
                    label="Type"
                    select
                    value={selected.type ?? "test"}
                    onChange={(e) =>
                      setSelected({ ...selected, type: e.target.value })
                    }
                    sx={{ minWidth: 160 }}
                  >
                    {TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label="Module"
                    select
                    value={selected.module ?? 1}
                    onChange={(e) =>
                      setSelected({
                        ...selected,
                        module: Number(e.target.value),
                      })
                    }
                    sx={{ minWidth: 160 }}
                  >
                    {MODULES.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Box sx={{ flex: 1 }} />

                  {selected.id && (
                    <Tooltip title="Delete this question">
                      <Button
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => onDeleteQuestion(selected.id)}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={onSaveQuestion}
                  >
                    Save
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack
                alignItems="center"
                sx={{ py: 6, color: "text.secondary" }}
                spacing={1}
              >
                <QuizIcon color="disabled" fontSize="large" />
                <Typography variant="body1">
                  Select a question from the left, or click “New Question”.
                </Typography>
              </Stack>
            )}
          </Card>

          {/* Media & answers only if selected */}
          {selected && (
            <Card>
              <Stack spacing={2}>
                {/* Image picker with drag-drop */}
                <Stack spacing={1}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 800 }}
                  >
                    Image (optional)
                  </Typography>

                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems="flex-start"
                  >
                    <Box
                      ref={dropRef}
                      sx={{
                        p: 2,
                        border: "2px dashed #e5e7eb",
                        borderRadius: 2,
                        minHeight: 120,
                        minWidth: { xs: "100%", md: 260 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <Stack spacing={1} alignItems="center">
                        <ImageIcon color="action" />
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          Drag & drop an image here
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.disabled" }}
                        >
                          or
                        </Typography>
                        <Button
                          component="label"
                          variant="outlined"
                          size="small"
                        >
                          Browse…
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f)
                                setSelected({
                                  ...selected,
                                  _imageFile: f,
                                  _removeImage: false,
                                });
                            }}
                          />
                        </Button>
                      </Stack>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      {previewSrc ? (
                        <Box
                          sx={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <img
                            src={previewSrc}
                            alt="question"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 180,
                              borderRadius: 10,
                              border: "1px solid #e5e7eb",
                            }}
                          />
                          <Button
                            startIcon={<CloseIcon />}
                            color="warning"
                            size="small"
                            onClick={() =>
                              setSelected({
                                ...selected,
                                _removeImage: true,
                                _imageFile: null,
                                image_url: null,
                              })
                            }
                            sx={{ mt: 1 }}
                          >
                            Remove image
                          </Button>
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", mt: 1 }}
                        >
                          No image selected.
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Stack>

                <Divider />

                {/* Answers editor */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800, flex: 1 }}
                  >
                    Answers (toggle which are correct)
                  </Typography>
                  <Button onClick={onAddAnswer} startIcon={<AddIcon />}>
                    Add answer
                  </Button>
                </Stack>

                <Stack spacing={1}>
                  {(selected.answers || []).map((row, idx) => (
                    <Paper
                      key={row.id ?? `new-${idx}`}
                      sx={{
                        p: 1.2,
                        borderRadius: 2,
                        border: "1px solid #e5e7eb",
                        bgcolor: row.is_correct
                          ? "rgba(34,197,94,0.05)"
                          : "white",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ sm: "center" }}
                      >
                        <Chip
                          icon={row.is_correct ? <CheckIcon /> : null}
                          label={row.is_correct ? "Correct" : "Incorrect"}
                          color={row.is_correct ? "success" : "default"}
                          variant={row.is_correct ? "filled" : "outlined"}
                          onClick={() => {
                            const next = [...selected.answers];
                            next[idx] = {
                              ...row,
                              is_correct: !row.is_correct,
                            };
                            setSelected({ ...selected, answers: next });
                          }}
                          sx={{ mr: 1, minWidth: 120 }}
                        />

                        <Tooltip title="Make this the only correct answer (sets others to incorrect)">
                          <IconButton
                            onClick={() => {
                              const next = (selected.answers || []).map(
                                (a, i) => ({
                                  ...a,
                                  is_correct: i === idx,
                                })
                              );
                              setSelected({ ...selected, answers: next });
                            }}
                          >
                            <StarBorderIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <TextField
                          fullWidth
                          size="small"
                          label={`Answer ${
                            row.id ? `#${row.id}` : "(new)"
                          }`}
                          value={row.answer_text ?? ""}
                          onChange={(e) => {
                            const next = [...selected.answers];
                            next[idx] = {
                              ...row,
                              answer_text: e.target.value,
                            };
                            setSelected({ ...selected, answers: next });
                          }}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Save answer">
                          <IconButton
                            color="primary"
                            onClick={() => onSaveAnswer(row, idx)}
                          >
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete answer">
                          <IconButton
                            color="error"
                            onClick={() => onDeleteAnswer(row, idx)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            </Card>
          )}

          {/* Live preview */}
          {selected && (
            <Card sx={{ border: "1px dashed #e5e7eb", bgcolor: "white" }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <PreviewIcon color="action" />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 800 }}
                >
                  Live Preview
                </Typography>
                <Chip
                  size="small"
                  label={
                    isMulti(selected)
                      ? "Select all that apply"
                      : "Select one"
                  }
                  variant="outlined"
                  color={isMulti(selected) ? "info" : "default"}
                  sx={{ ml: 1 }}
                />
              </Stack>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, mb: 1 }}
              >
                {selected.question_text || "Untitled question"}
              </Typography>

              {previewSrc && (
                <Box sx={{ mb: 1.5 }}>
                  <img
                    src={previewSrc}
                    alt="preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 220,
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                    }}
                  />
                </Box>
              )}

              {isMulti(selected) ? (
                <FormGroup>
                  {(selected.answers || []).map((a) => (
                    <FormControlLabel
                      key={a.id ?? a.answer_text}
                      control={<Checkbox disabled />}
                      label={a.answer_text || "Untitled"}
                    />
                  ))}
                </FormGroup>
              ) : (
                <RadioGroup>
                  {(selected.answers || []).map((a) => (
                    <FormControlLabel
                      key={a.id ?? a.answer_text}
                      value={a.id}
                      control={<Radio disabled />}
                      label={a.answer_text || "Untitled"}
                    />
                  ))}
                </RadioGroup>
              )}
            </Card>
          )}
        </Stack>
      </Stack>

      {/* global toasts + confirm dialog */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
      >
        <Alert severity={toast.sev} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>
      {ConfirmDialog}
    </Box>
  );
}
